import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface Store {
  name: string
  description: string
  delivery_days: number | null
  logo_url?: string
  sub_platforms?: string[]
}

interface StoreCategory {
  name: string
  description: string
  stores: Store[]
}

interface StoresPageProps extends SharedMarketingProps {
  categories: StoreCategory[]
  total_count: number
}

function StoreCard({ store }: { store: Store }) {
  const hasSubs = store.sub_platforms && store.sub_platforms.length > 0

  return (
    <div className="group relative flex h-full flex-col rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] transition-all duration-200 ease-out hover:-translate-y-1 hover:border-white/[0.16] hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)]">
      {/* Main content */}
      <div className="flex flex-1 flex-col items-center px-6 pt-8 text-center" style={{ paddingBottom: hasSubs ? '0' : '2rem' }}>
        {/* Logo */}
        <div className="flex h-12 items-center justify-center">
          {store.logo_url ? (
            <img
              src={store.logo_url}
              alt={store.name}
              className="h-10 max-w-[11rem] object-contain invert transition-opacity duration-200 group-hover:opacity-100"
            />
          ) : (
            <span
              className="font-[var(--wavbee-font-display)] text-xl font-bold tracking-tight text-white/40 transition-colors duration-200 group-hover:text-white/70"
            >
              {store.name}
            </span>
          )}
        </div>

        {/* Divider */}
        <div className="my-5 h-px w-10 bg-white/[0.08] transition-colors duration-200 group-hover:bg-[#ff6a1a]/30" />

        {/* Text */}
        <p className="text-[0.8rem] leading-relaxed text-white/50 transition-colors duration-200 group-hover:text-white/70">
          {store.description}
        </p>
      </div>

      {/* Sub-platforms footer */}
      {hasSubs && (
        <div className="mt-auto border-t border-white/[0.04] px-5 py-3.5">
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <span className="text-[0.6rem] font-medium uppercase tracking-widest text-white/25">
              Includes
            </span>
            {store.sub_platforms!.map((platform) => (
              <span
                key={platform}
                className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-[0.65rem] text-white/40 transition-colors duration-200 group-hover:bg-white/[0.1] group-hover:text-white/60"
              >
                {platform}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function LogoStrip({ stores }: { stores: Store[] }) {
  const withLogos = stores.filter((s) => s.logo_url)
  if (withLogos.length === 0) return null

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
      {withLogos.map((store) => (
        <img
          key={store.name}
          src={store.logo_url}
          alt={store.name}
          className="h-7 max-w-[9rem] object-contain invert opacity-40"
        />
      ))}
    </div>
  )
}

export default function Stores({ categories, total_count }: StoresPageProps) {
  const allStores = categories.flatMap((c) => c.stores)

  return (
    <>
      <AtmosphericHero minHeight="60vh" containerClassName="hero-content">
        <div className="hero-text">
          <span className="hero-eyebrow">Distribution Platforms</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}>
            Your music on <span className="grad-text">100+ platforms</span>
          </h1>
          <p className="hero-body">
            One upload. {total_count} direct integrations reaching 100+ platforms across every
            major streaming service, social platform, and digital store worldwide.
          </p>
          <div className="hero-ctas">
            <a href="/apply" className="marketing-button-primary">Apply Now</a>
            <a href="/pricing" className="marketing-button-secondary">See Plans</a>
          </div>
        </div>
      </AtmosphericHero>

      {/* All categories live in one dark section with a warm glow, full
          container width, internal space-y between categories — replaces
          the previous N-consecutive narrow per-category sections. */}
      <section className="marketing-section has-glow">
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-10rem', bottom: '-14rem' }} />
        <div className="container mx-auto px-6 space-y-20">
          {/* Logo cloud */}
          <ScrollReveal>
            <LogoStrip stores={allStores} />
          </ScrollReveal>

          {categories.map((category) => (
            <div key={category.name}>
              <ScrollReveal>
                <div className="marketing-section-header">
                  <div className="marketing-eyebrow-mono">{category.name}</div>
                  <p className="text-sm leading-relaxed text-white/60">{category.description}</p>
                </div>
              </ScrollReveal>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {category.stores.map((store, index) => (
                  <ScrollReveal key={store.name} delay={index * 0.04}>
                    <StoreCard store={store} />
                  </ScrollReveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA — cream variant so the page ends C, alternating from the
          dark category zone above. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal>
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">Ready to ship</div>
                <h2 className="marketing-section-title">Ready to reach every platform?</h2>
                <p className="marketing-lead max-w-2xl">
                  All plans include unlimited releases to every store listed above. No per-release fees.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="/apply" className="marketing-button-primary">Apply Now</a>
                <a href="/pricing" className="marketing-button-secondary">See Plans</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
